import { useEffect, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import { TrashIcon } from './icons'

export default function DimensionAllocations({
	selectedOptions = [],
	disabled = false,
	onChange,
	onRequestRemove,
	validPercentages,
}) {
	const [rows, setRows] = useState([])

	useEffect(() => {
		setRows((prev) => {
			const clamp = (v) => {
				const n = Number(v)
				if (!Number.isFinite(n)) return 0
				return Math.max(0, Math.min(100, n))
			}

			const prevMap = new Map(prev.map((r) => [String(r.id), r]))

			const next = (selectedOptions || []).map((opt, idx) => {
				// prefer id -> codeKey -> value
				const id = String(opt.id ?? opt.codeKey ?? opt.value)
				const existing = prevMap.get(id)

				// no override existing - save percent value and rest
				if (existing) return existing

				const label = opt.label ?? opt.codeValue ?? id
				const codeValue = opt.codeValue ?? null

				let percent
				if (opt.percent != null) {
					percent = clamp(opt.percent) // use percent from input values
				} else if (idx === 0) {
					percent = 100 // only when first item
				} else {
					percent = 0
				}

				return { id, label, codeValue, percent }
			})

			// If nothing changed (same items by reference), return prev to avoid
			// triggering the [rows] effect and breaking the render cycle.
			if (next.length === prev.length && next.every((r, i) => r === prev[i])) {
				return prev
			}
			return next
		})
	}, [selectedOptions])

	const onChangeRef = useRef(onChange)
	useEffect(() => {
		onChangeRef.current = onChange
	}, [onChange])

	useEffect(() => {
		onChangeRef.current?.(rows)
	}, [rows])

	const setPercent = (idx, val) => {
		const v = Math.max(0, Math.min(100, Number(val) || 0))
		setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, percent: v } : r)))
	}

	const removeRow = (id) => {
		onRequestRemove?.(id)
		setRows((prev) => prev.filter((r) => r.id !== id))
	}

	if (!rows.length) return null

	return (
		<div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-3 row-cols-xl-4 g-2 mt-2">
			{rows.map((item, idx) => (
				<div key={item.id} className="col">
					<div className="d-flex justify-content-between align-items-center gap-2 border rounded-1 p-2 h-100">
						<strong className="me-1 text-truncate" title={item.label}>
							{item.label}
						</strong>
						<div className="d-flex align-items-center gap-1 flex-shrink-0">
							<div className="input-group input-group-sm" style={{ maxWidth: 68 }}>
								<Form.Control
									className={`form-control form-control-sm text-center mb-0 bg-light border-${validPercentages ? '1' : 'danger'}`}
									type="text"
									inputMode="numeric"
									min={0}
									max={100}
									value={item.percent}
									disabled={disabled}
									onChange={(e) => setPercent(idx, e.target.value)}
								/>
								<span className="input-group-text bg-transparent">%</span>
							</div>

							<button
								type="button"
								className="btn btn-outline-secondary btn-sm"
								onClick={() => removeRow(item.id)}
								disabled={disabled}
								aria-label="Remove"
								title="Remove"
							>
								<TrashIcon />
							</button>
						</div>
					</div>
				</div>
			))}
		</div>
	)
}
